import { useState } from "react";
import { toast } from "sonner";
import { Paperclip, X, CheckCircle2, ArrowRight, Loader2 } from "lucide-react";

const TYPES = [
  "Opere murarie",
  "Ristrutturazione",
  "Cartongesso",
  "Intonaci e finiture",
  "Manutenzione edile",
  "Intervento per aziende / cantieri",
  "Altro",
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactForm = () => {
  const [form, setForm] = useState({ nome: "", azienda: "", telefono: "", email: "", localita: "", tipo: "", messaggio: "" });
  const [privacy, setPrivacy] = useState(false);
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...picked].slice(0, 5));
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!privacy) {
      setError("Per inviare la richiesta è necessario accettare l'informativa privacy.");
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("privacy", privacy ? "true" : "false");
      files.forEach((f) => fd.append("files", f));
      const res = await fetch(`${API}/contact`, { method: "POST", body: fd });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Errore durante l'invio della richiesta.");
      }
      setSent(true);
      toast.success("Richiesta inviata con successo.");
    } catch (err) {
      setError(err.message || "Errore durante l'invio. Riprova o contattaci telefonicamente.");
      toast.error("Invio non riuscito.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="contact-success-message" className="bg-card border border-forest-mid p-8 sm:p-10 text-center">
        <CheckCircle2 size={44} className="mx-auto text-forest-glow" />
        <h3 className="font-display font-bold text-xl sm:text-2xl text-white mt-5 uppercase tracking-tight">Richiesta inviata</h3>
        <p className="mt-4 text-cement leading-relaxed max-w-md mx-auto">
          Grazie per averci contattato. Abbiamo ricevuto la tua richiesta e ti ricontatteremo al più presto.
        </p>
        <button
          data-testid="contact-new-request-button"
          onClick={() => { setSent(false); setForm({ nome: "", azienda: "", telefono: "", email: "", localita: "", tipo: "", messaggio: "" }); setFiles([]); setPrivacy(false); }}
          className="btn-ghost mt-8"
        >
          Invia un'altra richiesta
        </button>
      </div>
    );
  }

  return (
    <form data-testid="contact-form" onSubmit={submit} className="bg-card border border-steel p-6 sm:p-8 space-y-5" noValidate={false}>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="cf-nome" className="block text-xs font-semibold uppercase tracking-[0.14em] text-metal/80 mb-2">Nome e cognome *</label>
          <input id="cf-nome" data-testid="contact-name-input" required value={form.nome} onChange={set("nome")} className="field-input" placeholder="Mario Rossi" />
        </div>
        <div>
          <label htmlFor="cf-azienda" className="block text-xs font-semibold uppercase tracking-[0.14em] text-metal/80 mb-2">Azienda</label>
          <input id="cf-azienda" data-testid="contact-company-input" value={form.azienda} onChange={set("azienda")} className="field-input" placeholder="Nome azienda (se applicabile)" />
        </div>
        <div>
          <label htmlFor="cf-tel" className="block text-xs font-semibold uppercase tracking-[0.14em] text-metal/80 mb-2">Telefono *</label>
          <input id="cf-tel" data-testid="contact-phone-input" required type="tel" value={form.telefono} onChange={set("telefono")} className="field-input" placeholder="320 000 0000" />
        </div>
        <div>
          <label htmlFor="cf-email" className="block text-xs font-semibold uppercase tracking-[0.14em] text-metal/80 mb-2">Email *</label>
          <input id="cf-email" data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} className="field-input" placeholder="nome@email.it" />
        </div>
        <div>
          <label htmlFor="cf-loc" className="block text-xs font-semibold uppercase tracking-[0.14em] text-metal/80 mb-2">Località</label>
          <input id="cf-loc" data-testid="contact-location-input" value={form.localita} onChange={set("localita")} className="field-input" placeholder="Es. Mestre (VE)" />
        </div>
        <div>
          <label htmlFor="cf-tipo" className="block text-xs font-semibold uppercase tracking-[0.14em] text-metal/80 mb-2">Tipo di intervento *</label>
          <select id="cf-tipo" data-testid="contact-type-select" required value={form.tipo} onChange={set("tipo")} className="field-input appearance-none cursor-pointer">
            <option value="" disabled>Seleziona un intervento</option>
            {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label htmlFor="cf-msg" className="block text-xs font-semibold uppercase tracking-[0.14em] text-metal/80 mb-2">Messaggio *</label>
        <textarea id="cf-msg" data-testid="contact-message-input" required rows={5} value={form.messaggio} onChange={set("messaggio")} className="field-input resize-y" placeholder="Descrivi brevemente il lavoro da realizzare..." />
      </div>
      <div>
        <input id="cf-files" data-testid="contact-file-input" type="file" multiple accept="image/*,.pdf,.doc,.docx" onChange={addFiles} className="hidden" />
        <label htmlFor="cf-files" data-testid="contact-file-label" className="inline-flex items-center gap-2.5 border border-dashed border-steel px-5 py-3.5 text-sm text-cement cursor-pointer transition-colors hover:border-forest-glow hover:text-forest-glow w-full justify-center">
          <Paperclip size={16} /> Allega fotografie o documenti (max 5)
        </label>
        {files.length > 0 && (
          <ul className="mt-3 space-y-2" data-testid="contact-file-list">
            {files.map((f, i) => (
              <li key={i} className="flex items-center justify-between gap-3 bg-graphite border border-steel px-4 py-2.5 text-xs text-metal/85">
                <span className="truncate">{f.name} <span className="text-cement/60">({(f.size / 1024 / 1024).toFixed(1)} MB)</span></span>
                <button type="button" data-testid={`contact-file-remove-${i}`} onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))} aria-label={`Rimuovi ${f.name}`} className="text-cement hover:text-red-400 transition-colors shrink-0">
                  <X size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="flex items-start gap-3">
        <input id="cf-privacy" data-testid="contact-privacy-checkbox" type="checkbox" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} className="mt-1 w-4 h-4 shrink-0 accent-[#2A594B] cursor-pointer" required />
        <label htmlFor="cf-privacy" className="text-xs text-cement leading-relaxed cursor-pointer">
          Ho letto l'informativa privacy e acconsento al trattamento dei dati personali. *
        </label>
      </div>
      {error && <p data-testid="contact-error-message" className="text-sm text-red-400 border border-red-900/50 bg-red-950/30 px-4 py-3">{error}</p>}
      <button type="submit" data-testid="contact-submit-button" disabled={sending} className="btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed">
        {sending ? (<><Loader2 size={16} className="animate-spin" /> Invio in corso...</>) : (<>Invia richiesta <ArrowRight size={16} /></>)}
      </button>
    </form>
  );
};
